import type { MemberDataFirestoreRecord } from "@albertine/types";
import { isAfter } from "date-fns";
import type React from "react";
import { useState } from "react";
import type { Agent } from "../../common/types/Agent";
import type { Conversation } from "../../common/types/Conversation";
import type { FirebaseTimestamp } from "../../common/types/FirebaseTimestamp";
import { InternalNote, type MessageOrInternalNote } from "../../common/types/Message";
import { isEmpty } from "../../common/utils/array.util";
import { isTypeOf } from "../../common/utils/zod.util";
import { fromTimestampToDate } from "../../utils/timestamp.util";
import { copyUrlToClipboard, isMobile } from "../../utils/window.util";
import { HoverMenu, HoverMenuItem } from "../Common/HoverMenu";
import Icon from "../Common/Icon";
import Tooltip from "../Common/Tooltip";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import "./ChatMessageActions.css";
import routes from "../../utils/routes";
import { useChatSelection } from "./ChatSelectionContext";
import { useCreateRequestFromMessages } from "./useCreateRequestFromMessages";

type DisabledAction = {
    dataTooltip: string;
    action: () => void;
};

type Props = {
    children: React.ReactNode;
    currentMember: MemberDataFirestoreRecord;
    currentAgent: Agent;
    messageBase: MessageOrInternalNote;
    conversation: Conversation;
    onResendNotifications?: () => void;
    onEditTopic?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    onRedact?: () => void;
    className?: string;
    disabledActions: DisabledAction[];
};

function hasMemberSeenMessage(
    lastSeenMessageDateByMember: FirebaseTimestamp | null | undefined,
    messageSentAt: FirebaseTimestamp | null | undefined,
): boolean {
    return lastSeenMessageDateByMember && messageSentAt
        ? isAfter(
              fromTimestampToDate(lastSeenMessageDateByMember),
              fromTimestampToDate(messageSentAt),
          )
        : false;
}

function ChatMessageActions(props: Props) {
    const {
        className,
        currentMember,
        currentAgent,
        conversation,
        children,
        messageBase,
        onResendNotifications,
        onEditTopic,
        onEdit,
        onDelete,
        onRedact,
        disabledActions,
    } = props;
    const [actionsVisible, setActionsVisible] = useState(false);

    const { selecting } = useChatSelection();
    const { createRequestFromMessages, isPending: createRequestSpinner } =
        useCreateRequestFromMessages({
            memberId: currentMember.id,
            assignedToAgentId: currentAgent.id,
        });

    const messageUrl = routes.chatMessage(
        currentMember.id,
        messageBase.id,
        messageBase.request || undefined,
    );

    const onEditActionDisabled = disabledActions.find((f) => f.action === onEdit);
    const editDisabled: boolean = onEditActionDisabled !== undefined;
    // TODO When app sends correct request with booking confirmation message,
    // add that to this condition
    const editTopicDisabled = !!(
        messageBase.bookingSuggestions && messageBase.bookingSuggestions.length > 0
    );

    const isInternalNote = isTypeOf(messageBase, InternalNote);

    function alertIfUserHasSeen() {
        if (
            !isInternalNote &&
            hasMemberSeenMessage(
                conversation.lastSeenMessageDateByMember,
                messageBase.sentAt,
            )
        ) {
            window.alert(
                "Warning: Member has already seen the message you are going to edit. You may still edit the message.",
            );
        }
    }

    const canCreateRequest = isEmpty(messageBase?.bookingSuggestions);

    return (
        <div
            className="chat-message__outer"
            tabIndex={0}
            {...(isMobile()
                ? {
                      onClick: () => {
                          setActionsVisible(!actionsVisible);
                      },
                  }
                : {
                      onFocus: () => {
                          setActionsVisible(true);
                      },
                      onBlur: () => {
                          setActionsVisible(false);
                      },
                      onMouseOver: () => {
                          setActionsVisible(true);
                      },
                      onMouseLeave: () => {
                          setActionsVisible(false);
                      },
                  })}
        >
            <div className={className}>
                {actionsVisible && !selecting && (
                    <div className="chat-message__actions-container">
                        <HoverMenu>
                            {!messageBase.sentByAgent &&
                                currentAgent.permissionGroup === "admin" && (
                                    <Tooltip label="Redact sensitive data">
                                        <HoverMenuItem>
                                            <div onClick={onRedact}>
                                                <Icon.Problem.Medium.E50 />
                                            </div>
                                        </HoverMenuItem>
                                    </Tooltip>
                                )}
                            <Tooltip label="Copy link">
                                <HoverMenuItem>
                                    <div
                                        onClick={() => {
                                            if (messageUrl) {
                                                copyUrlToClipboard(messageUrl);
                                            }
                                        }}
                                    >
                                        <Icon.Link.Medium.N20 />
                                    </div>
                                </HoverMenuItem>
                            </Tooltip>
                            {!!onResendNotifications && (
                                <Tooltip label="Resend notification">
                                    <HoverMenuItem>
                                        <div
                                            className="chat-message__action"
                                            onClick={onResendNotifications}
                                        >
                                            <Icon.Notifications.Medium.N20 />
                                        </div>
                                    </HoverMenuItem>
                                </Tooltip>
                            )}
                            {!!onEditTopic && (
                                <Tooltip label="Edit topic">
                                    <HoverMenuItem disabled={editTopicDisabled}>
                                        <div
                                            onClick={() => {
                                                if (!editTopicDisabled && onEditTopic) {
                                                    onEditTopic();
                                                }
                                            }}
                                        >
                                            <Icon.Label.Medium.N20 />
                                        </div>
                                    </HoverMenuItem>
                                </Tooltip>
                            )}
                            {!!messageBase.sentByAgent && !!onEdit && (
                                <Tooltip
                                    label={
                                        onEditActionDisabled
                                            ? onEditActionDisabled.dataTooltip
                                            : "Edit message"
                                    }
                                >
                                    <HoverMenuItem disabled={editDisabled}>
                                        <div
                                            onClick={() => {
                                                if (onEditActionDisabled) return;
                                                alertIfUserHasSeen();
                                                setActionsVisible(false);
                                                onEdit();
                                            }}
                                        >
                                            <Icon.Edit.Medium.N20 />
                                        </div>
                                    </HoverMenuItem>
                                </Tooltip>
                            )}
                            {!!onDelete && (
                                <Tooltip label="Delete message">
                                    <HoverMenuItem>
                                        <div onClick={onDelete}>
                                            <Icon.Delete.Medium.E50 />
                                        </div>
                                    </HoverMenuItem>
                                </Tooltip>
                            )}
                            {!isInternalNote && (
                                <Tooltip label="New request" placement="top-right">
                                    <HoverMenuItem disabled={!canCreateRequest}>
                                        <div
                                            onClick={() => {
                                                if (canCreateRequest) {
                                                    createRequestFromMessages([
                                                        messageBase.id,
                                                    ]);
                                                }
                                            }}
                                        >
                                            {createRequestSpinner ? (
                                                <LoadingSpinner />
                                            ) : (
                                                <Icon.Add.Medium.P50 />
                                            )}
                                        </div>
                                    </HoverMenuItem>
                                </Tooltip>
                            )}
                        </HoverMenu>
                    </div>
                )}
            </div>
            {children}
        </div>
    );
}

export default ChatMessageActions;
